import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import apiService from '../../services/api';

const AddStatementModal = ({ isOpen, onClose, onStatementAdded, accountId = null }) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [formData, setFormData] = useState({
    account_id: accountId || '',
    statement_date: '',
    closing_date: '',
    due_date: '',
    statement_balance: '',
    minimum_payment: '',
    previous_balance: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to calculate due date based on payment_due_day and closing date
  const calculateDueDate = (closingDate, paymentDueDay) => {
    if (!closingDate || !paymentDueDay) return null;

    const closing = new Date(closingDate + 'T00:00:00');
    // Due date is in the month after the closing date
    let dueMonth = closing.getMonth() + 1;
    let dueYear = closing.getFullYear();

    // Handle year rollover
    if (dueMonth > 11) {
      dueMonth = 0;
      dueYear++;
    }

    // Create the due date
    let dueDate = new Date(dueYear, dueMonth, paymentDueDay);

    // If the payment_due_day doesn't exist in that month (e.g., 31st in February),
    // use the last day of the month
    if (dueDate.getMonth() !== dueMonth) {
      dueDate = new Date(dueYear, dueMonth + 1, 0); // Last day of month
    }

    // If due date falls on a weekend, move to next business day
    const dayOfWeek = dueDate.getDay();
    if (dayOfWeek === 0) { // Sunday
      dueDate.setDate(dueDate.getDate() + 1);
    } else if (dayOfWeek === 6) { // Saturday
      dueDate.setDate(dueDate.getDate() + 2);
    }

    // Format as YYYY-MM-DD for input
    return dueDate.toISOString().split('T')[0];
  };

  // Get the selected account
  const selectedAccount = accounts.find(a => a.id === parseInt(formData.account_id));

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (accountId) {
      setFormData(prev => ({
        ...prev,
        account_id: accountId
      }));
    }
  }, [accountId]);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const data = await apiService.getAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };

      // Auto-calculate due date when closing_date or account changes
      if (name === 'closing_date' || name === 'account_id') {
        const account = name === 'account_id'
          ? accounts.find(a => a.id === parseInt(value))
          : accounts.find(a => a.id === parseInt(prev.account_id));

        const closingDate = name === 'closing_date' ? value : prev.closing_date;

        if (account?.payment_due_day && closingDate) {
          const calculatedDueDate = calculateDueDate(closingDate, account.payment_due_day);
          if (calculatedDueDate) {
            updated.due_date = calculatedDueDate;
          }
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.account_id) {
        throw new Error('Please select an account');
      }
      if (!formData.statement_date || !formData.closing_date || !formData.due_date) {
        throw new Error('Statement date, closing date, and due date are required');
      }
      if (!formData.statement_balance) {
        throw new Error('Statement balance is required');
      }

      // Format data for API
      const statementData = {
        account_id: parseInt(formData.account_id),
        statement_date: formData.statement_date,
        closing_date: formData.closing_date,
        due_date: formData.due_date,
        statement_balance: parseFloat(formData.statement_balance),
        minimum_payment: formData.minimum_payment ? parseFloat(formData.minimum_payment) : null,
        previous_balance: formData.previous_balance ? parseFloat(formData.previous_balance) : null,
        notes: formData.notes || null
      };

      await apiService.createStatement(statementData);

      // Call the callback to notify parent
      if (onStatementAdded) {
        await onStatementAdded();
      }

      // Reset form and close modal
      setFormData({
        account_id: accountId || '',
        statement_date: '',
        closing_date: '',
        due_date: '',
        statement_balance: '',
        minimum_payment: '',
        previous_balance: '',
        notes: ''
      });
      onClose();
    } catch (err) {
      console.error('Error creating statement:', err);
      setError(err.message || 'Failed to create statement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      size="lg"
      showCloseButton={false}
      closeOnBackdrop={!loading}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Add Statement</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-700 text-sm">{error}</div>
          </div>
        )}

        <div>
          <label htmlFor="account_id" className="block text-sm font-medium text-gray-700 mb-2">
            Account *
          </label>
          <select
            id="account_id"
            name="account_id"
            required
            value={formData.account_id}
            onChange={handleInputChange}
            disabled={loadingAccounts || accountId}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Select an account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} ({account.institution})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="statement_date" className="block text-sm font-medium text-gray-700 mb-2">
              Statement Date *
            </label>
            <input
              type="date"
              id="statement_date"
              name="statement_date"
              required
              value={formData.statement_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Date statement was issued</p>
          </div>

          <div>
            <label htmlFor="closing_date" className="block text-sm font-medium text-gray-700 mb-2">
              Closing Date *
            </label>
            <input
              type="date"
              id="closing_date"
              name="closing_date"
              required
              value={formData.closing_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Last day of billing period</p>
          </div>

          <div>
            <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-2">
              Due Date *
              {selectedAccount?.payment_due_day && (
                <span className="ml-2 text-xs text-green-600 font-normal">
                  (auto-calculated)
                </span>
              )}
            </label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              required
              value={formData.due_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {selectedAccount?.payment_due_day
                ? `Due day ${selectedAccount.payment_due_day} - adjusted to next business day if weekend`
                : 'Payment due date (set due day in account settings to auto-calculate)'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="statement_balance" className="block text-sm font-medium text-gray-700 mb-2">
              Statement Balance *
            </label>
            <input
              type="number"
              id="statement_balance"
              name="statement_balance"
              step="0.01"
              required
              value={formData.statement_balance}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Total amount due</p>
          </div>

          <div>
            <label htmlFor="minimum_payment" className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Payment
            </label>
            <input
              type="number"
              id="minimum_payment"
              name="minimum_payment"
              step="0.01"
              value={formData.minimum_payment}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum payment required</p>
          </div>

          <div>
            <label htmlFor="previous_balance" className="block text-sm font-medium text-gray-700 mb-2">
              Previous Balance
            </label>
            <input
              type="number"
              id="previous_balance"
              name="previous_balance"
              step="0.01"
              value={formData.previous_balance}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">Previous statement balance</p>
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows="3"
            value={formData.notes}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Add any additional notes..."
          />
        </div>

        <div className="flex items-center justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Statement'}
          </button>
        </div>
      </form>
    </BaseModal>
  );
};

export default AddStatementModal;
